import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import assetUrl from '../api/assetUrl';
import { useNotify, errorMessage } from '../components/Toast';
import './Common.css';
import './Orders.css';

// Human-readable French labels for the various enum columns.
const STATUS_LABELS = {
  pending: 'En attente',
  accepted: 'Accepté',
  finished: 'Terminé',
  canceled: 'Annulé',
};
const STATUS_OPTIONS = ['pending', 'accepted', 'finished', 'canceled'];

const PAYMENT_METHOD_LABELS = {
  cod: 'Paiement à la livraison',
  cmi: 'Carte bancaire (CMI)',
};
const PAYMENT_STATUS_LABELS = {
  pending: 'En attente',
  paid: 'Payé',
  failed: 'Échoué',
  refunded: 'Remboursé',
};

const money = (v) =>
  `${Number(v || 0).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} MAD`;

const Orders = () => {
  const notify = useNotify();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get('/orders', {
        params: { search: searchTerm, status: statusFilter, per_page: 100 },
      });
      setOrders(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des commandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const { data } = await axios.patch(`/orders/${orderId}/status`, { status: newStatus });
      const updated = data.order;
      // Keep the table and (if open) the modal in sync without a full refetch.
      setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, ...updated } : o)));
      setSelectedOrder((prev) => (prev && prev.id === orderId ? { ...prev, ...updated } : prev));
      notify.success('Statut mis à jour');
    } catch (error) {
      notify.error(errorMessage(error, 'Erreur lors de la mise à jour du statut'));
    }
  };

  const openModal = async (order) => {
    // Show immediately with the list data, then enrich with the full detail
    // (city, product images, variant, payment…) fetched from the API.
    setSelectedOrder(order);
    setIsModalOpen(true);
    setDetailLoading(true);
    try {
      const { data } = await axios.get(`/orders/${order.id}`);
      setSelectedOrder(data);
    } catch (error) {
      // Non-fatal: keep the list data already shown.
      console.error('Erreur lors du chargement des détails:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  const openImageModal = (imageUrl) => setPreviewImage(imageUrl);
  const closeImageModal = () => setPreviewImage(null);

  const copyOrderCode = (code) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code).then(
        () => notify.success('Code commande copié'),
        () => {}
      );
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="header-left">
          <h2>Commandes</h2>
          <p>Gérer les commandes clients</p>
        </div>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Rechercher par code commande, client..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && fetchOrders()}
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Tous les Statuts</option>
          <option value="pending">En Attente</option>
          <option value="accepted">Accepté</option>
          <option value="finished">Terminé</option>
          <option value="canceled">Annulé</option>
        </select>
        <button className="btn btn-secondary" onClick={fetchOrders}>Rechercher</button>
      </div>

      {loading ? (
        <div className="loading-state">Chargement...</div>
      ) : (
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Code Commande</th>
                <th>Client</th>
                <th>Téléphone</th>
                <th>Image</th>
                <th>Produit</th>
                <th>Quantité</th>
                <th>Total</th>
                <th>Statut</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order.id}>
                  <td><strong>{order.order_code}</strong></td>
                  <td>{order.customer_name}</td>
                  <td>{order.customer_phone}</td>
                  <td>
                    {order.product?.main_image_url ? (
                      <img
                        src={assetUrl(order.product.main_image_url)}
                        alt={order.product.ref}
                        style={{
                          width: '50px',
                          height: '50px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid #e0e0e0',
                          cursor: 'pointer',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openImageModal(assetUrl(order.product.main_image_url));
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '50px',
                        height: '50px',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999',
                        fontSize: '10px',
                      }}>
                        N/A
                      </div>
                    )}
                  </td>
                  <td>{order.product?.ref}</td>
                  <td>{order.quantity}</td>
                  <td>{order.total_price}</td>
                  <td>
                    <select
                      className={`status-badge ${order.status}`}
                      value={order.status}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleStatusChange(order.id, e.target.value);
                      }}
                    >
                      <option value="pending">En Attente</option>
                      <option value="accepted">Accepté</option>
                      <option value="finished">Terminé</option>
                      <option value="canceled">Annulé</option>
                    </select>
                  </td>
                  <td>{new Date(order.created_at).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <button
                      className="btn btn-secondary btn-sm"
                      title="Voir la commande"
                      onClick={() => openModal(order)}
                    >
                      Voir
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && selectedOrder && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content order-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="order-modal-title">
                <h3>Détails de la commande</h3>
                <button
                  type="button"
                  className="order-code-chip"
                  title="Copier le code"
                  onClick={() => copyOrderCode(selectedOrder.order_code)}
                >
                  {selectedOrder.order_code}
                  <span className="copy-icon" aria-hidden="true">⧉</span>
                </button>
              </div>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="modal-body">
              {detailLoading && (
                <div className="order-detail-loading">Chargement des détails…</div>
              )}

              {/* Product summary card */}
              <div className="order-product-card">
                {selectedOrder.product?.main_image_url ? (
                  <img
                    src={assetUrl(selectedOrder.product.main_image_url)}
                    alt={selectedOrder.product.ref}
                    className="order-product-thumb"
                    onClick={() => openImageModal(assetUrl(selectedOrder.product.main_image_url))}
                  />
                ) : (
                  <div className="order-product-thumb order-product-thumb--empty">N/A</div>
                )}
                <div className="order-product-info">
                  <div className="order-product-ref">{selectedOrder.product?.ref || '—'}</div>
                  {selectedOrder.product?.marque_nom && (
                    <div className="order-product-brand">{selectedOrder.product.marque_nom}</div>
                  )}
                  {(selectedOrder.variant_color || selectedOrder.variant_size) && (
                    <div className="order-variant-tags">
                      {selectedOrder.variant_color && (
                        <span className="variant-tag">Couleur : {selectedOrder.variant_color}</span>
                      )}
                      {selectedOrder.variant_size && (
                        <span className="variant-tag">Taille : {selectedOrder.variant_size}</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="order-product-qty">
                  <span className="qty-badge">× {selectedOrder.quantity}</span>
                </div>
              </div>

              {/* Status + inline edit */}
              <div className="order-section">
                <div className="order-section-title">Statut</div>
                <div className="order-status-row">
                  <span className={`status-badge ${selectedOrder.status}`}>
                    {STATUS_LABELS[selectedOrder.status] || selectedOrder.status}
                  </span>
                  <select
                    className={`status-badge ${selectedOrder.status} order-status-select`}
                    value={selectedOrder.status}
                    onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Customer */}
              <div className="order-section">
                <div className="order-section-title">Client</div>
                <div className="order-grid">
                  <div className="order-field">
                    <span className="order-label">Nom</span>
                    <span className="order-value">{selectedOrder.customer_name || '—'}</span>
                  </div>
                  <div className="order-field">
                    <span className="order-label">Téléphone</span>
                    <span className="order-value">
                      {selectedOrder.customer_phone ? (
                        <a href={`tel:${selectedOrder.customer_phone}`}>{selectedOrder.customer_phone}</a>
                      ) : '—'}
                    </span>
                  </div>
                  <div className="order-field order-field--wide">
                    <span className="order-label">Email</span>
                    <span className="order-value">
                      {selectedOrder.customer_email ? (
                        <a href={`mailto:${selectedOrder.customer_email}`}>{selectedOrder.customer_email}</a>
                      ) : '—'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Delivery */}
              <div className="order-section">
                <div className="order-section-title">Livraison</div>
                <div className="order-grid">
                  <div className="order-field order-field--wide">
                    <span className="order-label">Adresse</span>
                    <span className="order-value">{selectedOrder.shipping_address || '—'}</span>
                  </div>
                  <div className="order-field">
                    <span className="order-label">Ville</span>
                    <span className="order-value">{selectedOrder.city?.name || '—'}</span>
                  </div>
                  <div className="order-field">
                    <span className="order-label">Code postal</span>
                    <span className="order-value">{selectedOrder.postal_code || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Payment / totals */}
              <div className="order-section">
                <div className="order-section-title">Paiement</div>
                <div className="order-grid">
                  <div className="order-field">
                    <span className="order-label">Méthode</span>
                    <span className="order-value">
                      {PAYMENT_METHOD_LABELS[selectedOrder.payment_method] || selectedOrder.payment_method || '—'}
                    </span>
                  </div>
                  <div className="order-field">
                    <span className="order-label">Paiement</span>
                    <span className="order-value">
                      <span className={`pay-badge pay-${selectedOrder.payment_status || 'pending'}`}>
                        {PAYMENT_STATUS_LABELS[selectedOrder.payment_status] || selectedOrder.payment_status || '—'}
                      </span>
                    </span>
                  </div>
                </div>

                <div className="order-totals">
                  <div className="order-total-row">
                    <span>Sous-total</span>
                    <span>{money(selectedOrder.subtotal ?? (selectedOrder.total_price - (selectedOrder.shipping_price || 0)))}</span>
                  </div>
                  <div className="order-total-row">
                    <span>Livraison</span>
                    <span>{money(selectedOrder.shipping_price)}</span>
                  </div>
                  <div className="order-total-row order-total-row--grand">
                    <span>Total</span>
                    <span>{money(selectedOrder.total_price)}</span>
                  </div>
                </div>
              </div>

              {/* Meta */}
              <div className="order-section">
                <div className="order-section-title">Informations</div>
                <div className="order-grid">
                  <div className="order-field">
                    <span className="order-label">Créée le</span>
                    <span className="order-value">
                      {selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleString('fr-FR') : '—'}
                    </span>
                  </div>
                  <div className="order-field">
                    <span className="order-label">Mise à jour</span>
                    <span className="order-value">
                      {selectedOrder.updated_at ? new Date(selectedOrder.updated_at).toLocaleString('fr-FR') : '—'}
                    </span>
                  </div>
                  <div className="order-field order-field--wide">
                    <span className="order-label">Notes</span>
                    <span className="order-value order-notes">{selectedOrder.notes || 'Aucune note'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {selectedOrder.customer_phone && (
                <a
                  className="btn btn-secondary"
                  href={`https://wa.me/${String(selectedOrder.customer_phone).replace(/[^0-9]/g, '')}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  WhatsApp
                </a>
              )}
              <button className="btn btn-secondary" onClick={closeModal}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {previewImage && (
        <div className="modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeImageModal}>×</button>
            <img src={previewImage} alt="Aperçu du produit" className="image-preview" />
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
